<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox, isAnEmailChannel } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, priority, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    priority,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});
const widgetIdentityStatus = computed(
  () => chatMetadata.value?.widget_identity_status
);
const hasWidgetIdentityStatus = computed(() =>
  ['authenticated', 'visitor'].includes(widgetIdentityStatus.value)
);
const widgetIdentityLabel = computed(() => {
  if (widgetIdentityStatus.value === 'authenticated') {
    return t('CONVERSATION.AUTHENTICATED');
  }
  return t('CONVERSATION.VISITOR');
});

const mailSubject = computed(() => {
  if (!isAnEmailChannel.value) return '';
  return props.chat.additional_attributes?.mail_subject;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);
const protocol = computed(
  () => props.chat.additional_attributes?.protocol || ''
);
const maskedProtocol = computed(() => {
  if (!protocol.value) return '';
  if (protocol.value.length <= 9) return '••••';

  return `${protocol.value.slice(0, 5)}••••••${protocol.value.slice(-4)}`;
});
const isGeneratingProtocol = ref(false);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

const copyProtocol = async () => {
  if (!protocol.value) return;
  try {
    await copyTextToClipboard(protocol.value);
    useAlert(t('CONVERSATION.HEADER.TICKET_COPY_SUCCESS'));
  } catch (error) {
    // error
  }
};

const generateProtocol = async () => {
  if (isGeneratingProtocol.value) return;
  isGeneratingProtocol.value = true;
  try {
    await store.dispatch('generateProtocol', currentChat.value.id);
    useAlert(t('CONVERSATION.HEADER.TICKET_GENERATE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.HEADER.TICKET_GENERATE_ERROR'));
  } finally {
    isGeneratingProtocol.value = false;
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="me-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div class="flex flex-col items-start min-w-0 ms-2 overflow-hidden">
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!hasWidgetIdentityStatus && !isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
          <span
            v-if="hasWidgetIdentityStatus"
            class="shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium"
            :class="
              widgetIdentityStatus === 'authenticated'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{ widgetIdentityLabel }}
          </span>
        </div>

        <div
          class="flex items-center max-w-full gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">
            {{ t('CONVERSATION.HEADER.SEPARATOR') }}
          </span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span>{{ t('CONVERSATION.HEADER.SEPARATOR') }}</span>
          <button
            v-if="protocol"
            v-tooltip="t('CONVERSATION.HEADER.TICKET_COPY_TOOLTIP')"
            type="button"
            class="flex items-center gap-1 truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0"
            @click="copyProtocol"
          >
            <span class="truncate">
              {{
                t('CONVERSATION.HEADER.TICKET_DISPLAY', {
                  ticket: maskedProtocol,
                })
              }}
            </span>
            <fluent-icon size="12" icon="clipboard" class="shrink-0" />
          </button>
          <button
            v-else
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 disabled:opacity-50"
            :disabled="isGeneratingProtocol"
            @click="generateProtocol"
          >
            {{
              isGeneratingProtocol
                ? t('CONVERSATION.HEADER.TICKET_GENERATING')
                : t('CONVERSATION.HEADER.TICKET_GENERATE')
            }}
          </button>
          <span v-if="mailSubject">
            {{ t('CONVERSATION.HEADER.SEPARATOR') }}
          </span>
          <span
            v-if="mailSubject"
            v-tooltip="mailSubject"
            class="flex-1 min-w-16 truncate text-label-small text-n-slate-11"
          >
            {{ mailSubject }}
          </span>
          <span v-if="isSnoozed">
            {{ t('CONVERSATION.HEADER.SEPARATOR') }}
          </span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
